"use client";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { env } from "@/lib/environment";

import { Section } from "@/components/ui/Section";
import { Typography, Button } from "@/components/ui";

interface FeatureListAccordionProps {
  features: {
    id: number;
    title: string;
    description: string;
    image: string;
    link?: string;
    linkText?: string;
  }[];
  imgBaseUrl: string;
  autoSlide?: boolean;
}

export default function FeatureListAccordion({
  features,
  imgBaseUrl,
  autoSlide = true,
}: FeatureListAccordionProps) {
  const [activeContent, setActiveContent] = useState(0);

  useEffect(() => {
    if (autoSlide && features.length > 0) {
      const interval = setInterval(() => {
        setActiveContent(prev => (prev + 1) % features.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [autoSlide, features.length]);
  return (
    <Section variant="default">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-8">
          <div className="space-y-4">
            {features.map((content) => (
              <button
                key={content.id}
                className="w-full text-left border-b border-gray-200 pb-7 pt-5 transition-all duration-300 ease-in-out"
                onClick={() => setActiveContent(content.id)}
                aria-expanded={activeContent === content.id}
                aria-controls={`content-${content.id}`}
                type="button"
              >
                <Typography variant="h4" as="h3" className="flex justify-between">
                  {content.title}
                  <div className="relative">
                    <div className="w-6 h-[1px] bg-gray-500"></div>
                    <div className={`w-[1px] h-6 bg-gray-500 absolute top-[-35%] right-[50%] translate-x-[50%] transition-all duration-300 ease-in-out ${activeContent === content.id ? "hidden" : ""}`}></div>
                  </div>
                </Typography>
                <div
                  id={`content-${content.id}`}
                  className="overflow-hidden transition-all duration-300 ease-in-out pr-4"
                  style={{ height: activeContent === content.id ? "auto" : "0" }}
                  aria-hidden={activeContent !== content.id}
                >
                  <Typography variant="small" as="p" className="text-gray-700 mt-2">
                    {content.description}
                  </Typography>


                  {content.link && (
                    <Link 
                      href={content.link} 
                      className="link-primary inline-flex items-center gap-2 text-base font-medium py-2 mt-6 mb-4 min-h-[44px]"
                      tabIndex={activeContent === content.id ? 0 : -1}
                    >
                      {content.linkText}
                      <Image
                        src={env.NEXT_PUBLIC_CDN_URL + "/images/industries/enterpise/arrow-right-primary.Fw0PoVWIVDIu.svg"}
                        alt=""
                        aria-hidden="true"
                        width={18}
                        height={18}
                      />
                    </Link>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <Image
            src={imgBaseUrl + features[activeContent].image}
            alt={features[activeContent].title}
            title={features[activeContent].title}
            width={600}
            height={400}
            className="w-full h-auto "
          />
        </div>
      </div>
    </Section>
  );
}
